import { Box, Paper, Typography } from '@mui/material';
import type { FieldSpec, ProjectSchema } from '../../types';
import { isVisible } from '../../utils/expr';

/**
 * Print-only appendix that lists every parameter that produced the
 * report, grouped by calculator section. Hidden on screen (the user
 * already has the sidebar); shown in the PDF so readers can see
 * exactly which inputs generated the numbers they're reviewing.
 *
 * Forces a page break before itself so the summary lands on a clean
 * page, separate from the scaling chart.
 */

interface Props {
  schema: ProjectSchema;
  params: Record<string, unknown>;
}

function formatNumber(n: number): string {
  if (Number.isInteger(n)) return n.toLocaleString();
  // Trim trailing zeros after rounding to 2 dp.
  return Number(n.toFixed(2)).toString();
}

function formatValue(spec: FieldSpec, value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';

  if (spec.type === 'slider') {
    const multiplier = spec.multiplier ?? 1;
    const num = typeof value === 'number' ? value * multiplier : Number(value);
    if (!Number.isFinite(num)) return String(value);
    return spec.unit ? `${formatNumber(num)} ${spec.unit}` : formatNumber(num);
  }
  if (spec.type === 'number') {
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) return String(value);
    return spec.unit ? `${formatNumber(num)} ${spec.unit}` : formatNumber(num);
  }
  if (spec.type === 'select' || spec.type === 'toggle') {
    const opt = spec.options.find((o) => o.value === value);
    return opt?.label ?? String(value);
  }
  return String(value);
}

export function InputsSummary({ schema, params }: Props) {
  // Skip sections whose module is switched off — a disabled module is hidden
  // everywhere, including here, so the appendix matches the breakdown.
  const sections = schema.calculator.sections.filter((s) =>
    isVisible(s.visible_when, params),
  );

  return (
    <Paper
      className="print-only"
      data-pdf-page-break-before="true"
      sx={{
        mt: 3,
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
      elevation={0}
    >
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Inputs Used
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 2 }}
      >
        All parameters that generated this report.
      </Typography>

      {sections.map((section) => {
        const fields = [...section.fields, ...section.advanced_fields].filter(
          (f) => f.type !== 'derived' && isVisible(f.visible_when, params),
        );
        if (fields.length === 0) return null;

        return (
          <Box key={section.workflow_name} sx={{ mb: 2.5 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                color: 'primary.main',
                mb: 0.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: 0.5,
              }}
            >
              {section.title}
            </Typography>
            <Box>
              {fields.map((f) => (
                <Box
                  key={f.name}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 0.4,
                    borderBottom: '1px dotted',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {f.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatValue(f, params[f.name])}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
}
